import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Search, Loader2, Sparkles, AlertTriangle, ExternalLink, Check, Copy,
  Filter, ArrowUpRight, ChevronDown, ChevronUp, DollarSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/apiClient";

const LABEL_COLORS = {
  "Hot Lead": "bg-red-500/10 text-red-200 border-red-500/40",
  "Good Lead": "bg-emerald-500/10 text-emerald-200 border-emerald-500/40",
  "Moderate Lead": "bg-amber-500/10 text-amber-200 border-amber-500/40",
  "Low Priority": "bg-white/5 text-white/50 border-white/15",
};

const NICHE_PRESETS = [
  "MMA Gym", "Brazilian Jiu-Jitsu Academy", "Auto Detailing",
  "Roofing", "Pressure Washing", "Landscaping",
  "Personal Trainer", "Plumbing", "HVAC", "Electrician", "Barbershop",
];

export default function LeadGenerator() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    niche: "",
    city: "",
    state: "",
    count: 25,
    min_rating: "",
    max_rating: "",
    min_reviews: "",
    max_reviews: "",
    website_status: "all",
    keywords: "",
    excluded_keywords: "",
  });
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: c }, { data: h }] = await Promise.all([
          api.get("/admin/lead-generator/config"),
          api.get("/admin/lead-generator/history"),
        ]);
        setCfg(c);
        setHistory(h);
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      }
    })();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const openConfirm = (e) => {
    e.preventDefault();
    if (!form.niche.trim()) return toast.error("Enter a niche or keyword");
    if (!cfg?.google_places_configured) return toast.error("Google Places API key not configured");
    setConfirming(true);
  };

  const runSearch = async () => {
    setConfirming(false);
    setRunning(true);
    setResult(null);
    setSelected(new Set());
    setExpanded(new Set());
    try {
      const payload = {
        niche: form.niche.trim(),
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        count: Number(form.count),
        website_status: form.website_status,
      };
      if (form.min_rating) payload.min_rating = Number(form.min_rating);
      if (form.max_rating) payload.max_rating = Number(form.max_rating);
      if (form.min_reviews) payload.min_reviews = Number(form.min_reviews);
      if (form.max_reviews) payload.max_reviews = Number(form.max_reviews);
      if (form.keywords.trim()) payload.keywords = form.keywords.split(",").map((s) => s.trim()).filter(Boolean);
      if (form.excluded_keywords.trim()) payload.excluded_keywords = form.excluded_keywords.split(",").map((s) => s.trim()).filter(Boolean);

      const { data } = await api.post("/admin/lead-generator/search", payload);
      setResult(data);
      // Auto-select non-duplicates
      const auto = new Set(data.items.filter((it) => !it.already_in_crm).map((it) => it.place_id));
      setSelected(auto);
      toast.success(`Found ${data.returned} business${data.returned === 1 ? "" : "es"}.`);
      // refresh history
      const { data: h } = await api.get("/admin/lead-generator/history");
      setHistory(h);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setRunning(false);
    }
  };

  const toggleAll = () => {
    if (!result) return;
    if (selected.size === result.items.length) setSelected(new Set());
    else setSelected(new Set(result.items.map((it) => it.place_id)));
  };
  const toggleOne = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleExpand = (id) => {
    const s = new Set(expanded);
    s.has(id) ? s.delete(id) : s.add(id);
    setExpanded(s);
  };

  const saveSelected = async () => {
    if (!result) return;
    const items = result.items.filter((it) => selected.has(it.place_id) && !it.already_in_crm);
    if (items.length === 0) return toast.info("No new leads selected");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/lead-generator/save-items", {
        items, search_id: result.search_id,
      });
      toast.success(`Saved ${data.created} lead${data.created === 1 ? "" : "s"} to CRM${data.duplicates_skipped ? ` (${data.duplicates_skipped} duplicates skipped)` : ""}.`);
      // mark them in-place as already in crm
      setResult((r) => ({
        ...r,
        items: r.items.map((it) => selected.has(it.place_id) ? { ...it, already_in_crm: true } : it),
      }));
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const stats = useMemo(() => {
    if (!result) return null;
    const items = result.items;
    return {
      total: items.length,
      hot: items.filter((i) => i.lead_label === "Hot Lead").length,
      good: items.filter((i) => i.lead_label === "Good Lead").length,
      dupes: items.filter((i) => i.already_in_crm).length,
    };
  }, [result]);

  return (
    <div className="space-y-6" data-testid="lead-generator-page">
      <div>
        <span className="text-[11px] tracking-[0.22em] uppercase text-blue-400 font-medium">
          Prospecting
        </span>
        <h1 className="font-display text-3xl md:text-4xl mt-2">Lead Generator</h1>
        <p className="text-white/50 text-sm mt-1">
          Search public business data via Google Places, auto-score each result, and add qualified prospects to your CRM.
        </p>
      </div>

      {cfg && !cfg.google_places_configured && (
        <div className="card-soft rounded-2xl p-5 border-amber-500/30 bg-amber-500/5 flex items-start gap-3" data-testid="google-key-warning">
          <AlertTriangle size={16} className="text-amber-300 mt-0.5" />
          <div className="text-sm text-amber-100/90">
            <div className="font-medium">Google Places API key not configured</div>
            <p className="mt-1 text-amber-100/70 text-[13px] leading-relaxed">
              Add your key to <code className="bg-black/40 px-1.5 py-0.5 rounded">backend/.env</code> as <code className="bg-black/40 px-1.5 py-0.5 rounded">GOOGLE_MAPS_API_KEY=...</code> and restart the backend. Get one at{" "}
              <a href="https://console.cloud.google.com/apis/library/places-backend.googleapis.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">
                Google Cloud Console → Places API (New)
              </a>. Free tier includes ~$200/mo credit.
            </p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={openConfirm} className="card-soft rounded-2xl p-6" data-testid="lead-generator-form">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <F l="Niche / keyword *" full>
            <Input value={form.niche} onChange={set("niche")} placeholder="e.g. auto detailing" list="niche-presets" className="bg-[#0A0A0A] border-white/15 text-white h-11" data-testid="niche-input" />
            <datalist id="niche-presets">
              {NICHE_PRESETS.map((n) => <option key={n} value={n} />)}
            </datalist>
          </F>
          <F l="City"><Input value={form.city} onChange={set("city")} placeholder="Miami" className="bg-[#0A0A0A] border-white/15 text-white h-11" data-testid="city-input" /></F>
          <F l="State"><Input value={form.state} onChange={set("state")} placeholder="FL" className="bg-[#0A0A0A] border-white/15 text-white h-11" data-testid="state-input" /></F>
          <F l="How many">
            <Select value={String(form.count)} onValueChange={(v) => setForm({ ...form, count: Number(v) })}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/15 text-white h-11" data-testid="count-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-white/10 text-white">
                {(cfg?.count_options || [25, 50, 100]).map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} businesses</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <F l="Website status">
            <Select value={form.website_status} onValueChange={(v) => setForm({ ...form, website_status: v })}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/15 text-white h-11" data-testid="website-status-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-white/10 text-white">
                {(cfg?.website_status_options || [{ value: "all", label: "All businesses" }]).map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <F l="Min rating"><Input type="number" step="0.1" min="0" max="5" value={form.min_rating} onChange={set("min_rating")} placeholder="4.0" className="bg-[#0A0A0A] border-white/15 text-white h-11" /></F>
          <F l="Max rating"><Input type="number" step="0.1" min="0" max="5" value={form.max_rating} onChange={set("max_rating")} placeholder="5.0" className="bg-[#0A0A0A] border-white/15 text-white h-11" /></F>
          <F l="Min reviews"><Input type="number" min="0" value={form.min_reviews} onChange={set("min_reviews")} placeholder="10" className="bg-[#0A0A0A] border-white/15 text-white h-11" /></F>
          <F l="Max reviews"><Input type="number" min="0" value={form.max_reviews} onChange={set("max_reviews")} placeholder="1000" className="bg-[#0A0A0A] border-white/15 text-white h-11" /></F>
          <F l="Include keywords (comma-separated)" full>
            <Input value={form.keywords} onChange={set("keywords")} placeholder="mobile, luxury" className="bg-[#0A0A0A] border-white/15 text-white h-11" />
          </F>
          <F l="Exclude keywords (comma-separated)" full>
            <Input value={form.excluded_keywords} onChange={set("excluded_keywords")} placeholder="franchise, dealership" className="bg-[#0A0A0A] border-white/15 text-white h-11" />
          </F>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-xs text-white/40 max-w-md">
            Estimated Google API cost: about ${(form.count * 0.032 / 1000).toFixed(3)} for this search. You&apos;ll confirm before it runs.
          </p>
          <Button type="submit" disabled={running || !cfg?.google_places_configured} className="bg-blue-500 hover:bg-blue-600 text-white rounded-full h-11 px-6 disabled:opacity-40" data-testid="run-search-btn">
            {running ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Searching…</> : <><Search size={14} className="mr-1.5" /> Search</>}
          </Button>
        </div>
      </form>

      {/* Result */}
      {result && (
        <div className="space-y-4" data-testid="lead-generator-results">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Returned" value={stats.total} />
            <Stat label="Hot leads" value={stats.hot} tone="hot" />
            <Stat label="Good leads" value={stats.good} tone="good" />
            <Stat label="Already in CRM" value={stats.dupes} tone="muted" />
          </div>

          <div className="card-soft rounded-2xl overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-white/10">
              <div className="text-sm text-white/70">
                {selected.size} selected · {result.items.filter(i => selected.has(i.place_id) && !i.already_in_crm).length} savable
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={toggleAll} className="bg-transparent border-white/15 hover:bg-white/5 text-white rounded-md h-9">
                  {selected.size === result.items.length ? "Deselect all" : "Select all"}
                </Button>
                <Button size="sm" onClick={saveSelected} disabled={saving || selected.size === 0} className="bg-blue-500 hover:bg-blue-600 text-white rounded-md h-9 disabled:opacity-40" data-testid="save-selected-btn">
                  {saving ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <ArrowUpRight size={13} className="mr-1.5" />}
                  Add {selected.size ? `${selected.size} to CRM` : "to CRM"}
                </Button>
              </div>
            </div>

            <ul>
              {result.items.map((it) => {
                const isSel = selected.has(it.place_id);
                const isExp = expanded.has(it.place_id);
                return (
                  <li key={it.place_id} className="border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors" data-testid={`result-${it.place_id}`}>
                    <div className="flex items-start gap-3 p-4">
                      <Checkbox checked={isSel} onCheckedChange={() => toggleOne(it.place_id)} disabled={it.already_in_crm} className="mt-1.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium text-white truncate">{it.business_name || "Untitled"}</span>
                              {it.lead_label && (
                                <span className={`text-[10px] px-2 py-0.5 rounded-full border tracking-wide uppercase ${LABEL_COLORS[it.lead_label] || "bg-white/5 text-white/60 border-white/15"}`}>
                                  {it.lead_label} · {it.lead_score}
                                </span>
                              )}
                              {it.already_in_crm && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-200 uppercase tracking-wide">
                                  In CRM
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-white/50 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                              {it.rating != null && <span>★ {it.rating.toFixed(1)} · {it.review_count || 0} reviews</span>}
                              {it.city && <span>{it.city}{it.state ? `, ${it.state}` : ""}</span>}
                              {it.website && (
                                <a href={it.website} target="_blank" rel="noopener noreferrer" className="hover:text-blue-300 inline-flex items-center gap-1">
                                  {it.website.replace(/^https?:\/\//, "").slice(0, 40)}
                                  <ExternalLink size={10} />
                                </a>
                              )}
                              {it.phone && <span>{it.phone}</span>}
                            </div>
                          </div>
                          <button onClick={() => toggleExpand(it.place_id)} className="text-white/40 hover:text-white transition-colors shrink-0">
                            {isExp ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </div>

                        {isExp && (
                          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-medium">Why this scored {it.lead_score}</div>
                              <p className="mt-1.5 text-white/70">{it.score_explanation || "—"}</p>
                              {it.website_issues && it.website_issues.length > 0 && (
                                <ul className="mt-3 space-y-1">
                                  {it.website_issues.map((iss, i) => (
                                    <li key={i} className="text-xs text-red-200/80 flex items-start gap-2">
                                      <AlertTriangle size={11} className="mt-0.5 shrink-0" /> {iss}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.18em] text-blue-300 font-medium">Recommended service</div>
                              <p className="mt-1.5 text-white/80 font-medium">{it.recommended_service || "—"}</p>
                              <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-medium mt-3">Outreach angle</div>
                              <p className="mt-1.5 text-white/70">{it.outreach_angle || "—"}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
              {result.items.length === 0 && (
                <li className="p-10 text-center text-white/40 text-sm">No businesses matched. Try relaxing filters.</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Search history */}
      {history?.searches?.length > 0 && (
        <div className="card-soft rounded-2xl p-6" data-testid="search-history">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg text-white">Recent searches</h3>
            {history.usage_today?.total && (
              <span className="text-xs text-white/50 inline-flex items-center gap-1.5">
                <DollarSign size={11} /> {history.usage_today.total} Places API call{history.usage_today.total === 1 ? "" : "s"} today
              </span>
            )}
          </div>
          <ul className="space-y-2">
            {history.searches.slice(0, 6).map((s, i) => (
              <li key={i} className="text-xs text-white/60 flex justify-between gap-4 py-1 border-t border-white/5 first:border-t-0">
                <span className="truncate">
                  {s.query.niche} {s.query.city ? `in ${s.query.city}${s.query.state ? ", " + s.query.state : ""}` : ""} · {s.result_count} results
                </span>
                <span className="text-white/40 whitespace-nowrap">{new Date(s.at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Confirm dialog */}
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent className="bg-[#0F0F0F] border border-white/10 text-white max-w-md" data-testid="confirm-search-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Run paid API search?</DialogTitle>
            <DialogDescription className="text-white/60 mt-2">
              This will call the Google Places API and fetch up to <strong className="text-white">{form.count}</strong> businesses for <strong className="text-white">{form.niche || "(niche)"}</strong>
              {form.city ? <> in <strong className="text-white">{form.city}{form.state ? `, ${form.state}` : ""}</strong></> : null}.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 text-sm">
            <div className="flex justify-between text-white/60"><span>Estimated cost</span><span className="text-white font-medium">${(form.count * 0.032 / 1000).toFixed(3)}</span></div>
            <div className="flex justify-between text-white/60 mt-1"><span>Google Places calls</span><span className="text-white font-medium">~{Math.max(1, Math.ceil(form.count / 20))}</span></div>
          </div>
          <p className="text-xs text-white/45">Results deduplicated against your existing CRM automatically.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)} className="bg-transparent border-white/15 hover:bg-white/5 text-white">Cancel</Button>
            <Button onClick={runSearch} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="confirm-search-btn">
              <Sparkles size={14} className="mr-1.5" /> Run search
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ l, children, full }) {
  return (
    <div className={full ? "md:col-span-3" : ""}>
      <Label className="text-[11px] uppercase tracking-[0.18em] text-white/55">{l}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneCls = tone === "hot"
    ? "text-red-300"
    : tone === "good"
      ? "text-emerald-300"
      : tone === "muted"
        ? "text-white/50"
        : "text-white";
  return (
    <div className="card-soft rounded-xl p-4">
      <div className={`font-display text-2xl ${toneCls}`}>{value}</div>
      <div className="text-xs text-white/50 mt-1">{label}</div>
    </div>
  );
}
